import React from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { FormGroup, FormControl, FormLabel, Row, Col } from "react-bootstrap";
import { FiX } from "react-icons/fi";
import { BsFillPlusCircleFill } from "react-icons/bs";
import moment from "moment";
import { shiftsService } from "services";
import { errorFormating } from "utils/utils";
import SubmitButton from "components/SubmitButton";
import SwitchField from "components/SwitchField";
import PillToggleGroup from "components/PillToggleGroup";
import Pills from "components/Pills";
import FormTextField from "components/FormTextField";
import TimeIntervalCard from "components/TimeIntervalCard";
import TimeIntervalEditor from "components/TimeIntervalEditor";
import ShiftColorField from "components/Shifts/ShiftColorField";
import ContextualHelpBanner from "components/ContextualHelpBanner";
import InlineAlert from "components/InlineAlert";
import CatalogMultiSelect, {
	catalogChip,
} from "components/CatalogMultiSelect/CatalogMultiSelect";
import * as shiftsActions from "actions/shiftsActions";
import * as calendarsActions from "actions/calendarsActions";
import * as timesActions from "actions/timesActions";
import * as groupsActions from "actions/groupsActions";
import { convertToUTC, convertFromUTC } from "utils/datetime";
import { withTranslation } from "react-i18next";

const WORK_SHIFT_TYPES = ["Trabajo", "Automático"];

const NEW_SHIFT_TYPES = ["Trabajo", "Automático", "Ausencia", "Vacaciones"];

const buildTimeIntervalsByType = (shiftType, timeIntervals = []) => ({
	Trabajo: shiftType === "Trabajo" ? [...timeIntervals] : [],
	Automático: shiftType === "Automático" ? [...timeIntervals] : [],
});

// Paleta de colores para turnos
const SHIFT_COLORS = [
	"#DAFFF2",
	"#FFE6F1",
	"#FFF6DB",
	"#DEDAFE",
	"#F0DDFF",
	"#D7EAFF",
	"#FFEADC",
	"#FDDBFF",
	"#EED9CE",
	"#CFF2F9",
];

class ShiftEdit extends React.Component {
	constructor(props) {
		super(props);
		this.timeIntervalEditorRef = React.createRef();
		// console.log(props);

		const initialType =
			!props.shift &&
			props.initialType &&
			NEW_SHIFT_TYPES.includes(props.initialType)
				? props.initialType
				: "Trabajo";

		let shift = {
			type: initialType,
			name: "",
			description: "",
			background_color: "",
			border_color: "",
			time_intervals: [],
			allow_hourly_absence_request: false,
			send_payflow_on_leave: false,
			has_groups: false,
			group_ids: [],
			allow_overlap: false,
			max_per_year: -1,
			month_limit: -1,
			day_limit: -1,
			active: true,
		};

		let isEdit = false;
		let selected_day_types = [];
		let selected_groups = [];
		let show_timetype_disclaimer = false;
		const default_time_type = this.props.default_time_type;

		if (props.shift) {
			isEdit = true;
			shift = { ...props.shift };

			// Normalize type to canonical Spanish API value in case the API returns a localized string
			const TYPE_CANONICAL = {
				Trabajo: "Trabajo", Work: "Trabajo", Treball: "Trabajo", Attendance: "Trabajo",
				Automático: "Automático", Automatic: "Automático",
				Ausencia: "Ausencia", Absence: "Ausencia", Absent: "Ausencia", "Absència": "Ausencia",
				Vacaciones: "Vacaciones", Vacation: "Vacaciones", Vacances: "Vacaciones",
				Descanso: "Descanso", Rest: "Descanso", Descans: "Descanso",
			};
			if (shift.type && TYPE_CANONICAL[shift.type]) {
				shift.type = TYPE_CANONICAL[shift.type];
			} else if (!NEW_SHIFT_TYPES.includes(shift.type) && shift.type !== "Descanso") {
				shift.type = "Trabajo";
			}

			shift.send_payflow_on_leave = Boolean(shift.send_payflow_on_leave);

			if (shift.time_intervals === undefined) shift.time_intervals = [];
			else
				show_timetype_disclaimer = this.checkTimeTypeDisponibilidad(
					shift.time_intervals,
					shift.type
				);

			selected_day_types = shift.day_types;
			selected_groups = shift.groups || [];
		}

		if (!isEdit) {
			const { select_day_types } = this.props;
			//console.log(select_day_types);

			// Seleccionar todos los días de la semana (1-7)
			for (let i = 1; i <= 7; i++) {
				const day_type = select_day_types.find((item) => item.weekday === i);
				if (day_type) {
					selected_day_types.push(day_type);
				}
			}

			// Seleccionar también el tipo de día festivo
			const holiday_day_type = select_day_types.find(
				(item) => item.holiday === true
			);
			if (
				holiday_day_type &&
				!selected_day_types.find((item) => item.id === holiday_day_type.id)
			) {
				selected_day_types.push(holiday_day_type);
			}
		}

		if (!isEdit && props.initialInterval) {
			shift.type = "Trabajo";
		}

		let new_interval = this.buildDefaultNewInterval(
			shift.type,
			default_time_type,
			this.props.select_time_types
		);

		if (!isEdit && props.initialInterval) {
			new_interval = {
				...new_interval,
				start: props.initialInterval.start || "",
				end: props.initialInterval.end || "",
			};
		}

		this.state = {
			isEdit,
			isLoading: false,
			isBorderPickerVisible: false,
			isBackgroundPickerVisible: false,
			submitted: false,
			errors: {},
			new_day_type: {},
			new_interval,
			selected_day_types,
			selected_groups,
			new_interval_errors: {},
			shift,
			time_intervals_by_type: buildTimeIntervalsByType(
				shift.type,
				shift.time_intervals
			),
			show_timetype_disclaimer,
			isAddingInterval: !isEdit,
		};
	}

	buildDefaultNewInterval = (
		shiftType,
		defaultTimeType = this.props.default_time_type,
		selectTimeTypes = this.props.select_time_types
	) => {
		let time_type_id = "";

		if (shiftType === "Automático") {
			const availabilityType = selectTimeTypes.find(
				(t) => t.name === "Disponibilidad"
			);
			if (availabilityType) {
				time_type_id = availabilityType.id;
			}
		} else if (shiftType === "Trabajo") {
			const trabajoType = selectTimeTypes.find((t) => t.name === "Trabajo");
			const workType =
				trabajoType ||
				(defaultTimeType && defaultTimeType.name !== "Disponibilidad"
					? defaultTimeType
					: selectTimeTypes.find(
							(t) => t.work_time && t.name !== "Disponibilidad"
					  ));

			if (workType) {
				time_type_id = workType.id;
			}
		} else if (defaultTimeType) {
			time_type_id = defaultTimeType.id;
		}

		return {
			time_type_id: time_type_id !== "" ? String(time_type_id) : "",
			start: "",
			end: "",
		};
	};

	syncInitialIntervalPreset = () => {
		if (this.state.isEdit || !this.props.initialInterval) return;

		const { select_time_types, default_time_type } = this.props;
		if (!select_time_types || select_time_types.length === 0) return;

		const defaultInterval = this.buildDefaultNewInterval(
			"Trabajo",
			default_time_type,
			select_time_types
		);

		if (!defaultInterval.time_type_id) return;

		this.setState((prevState) => {
			if (prevState.new_interval.time_type_id) {
				return null;
			}

			return {
				shift: { ...prevState.shift, type: "Trabajo" },
				new_interval: {
					...prevState.new_interval,
					time_type_id: defaultInterval.time_type_id,
				},
			};
		});
	};

	focusTimeIntervalEditorStart = () => {
		requestAnimationFrame(() => {
			const editorEl = this.timeIntervalEditorRef.current;
			if (!editorEl) return;

			editorEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
			const startInput = editorEl.querySelector("input#start");
			if (startInput) {
				startInput.focus();
			}
		});
	};

	focusNameField = () => {
		requestAnimationFrame(() => {
			const nameInput = document.getElementById("name");
			if (nameInput) {
				nameInput.focus();
			}
		});
	};

	componentDidMount() {
		// carga las listas solo si vienen vacías (cuando se viene desde Plan)
		const {
			shift_types,
			select_day_types,
			groups,
			select_time_types,
			default_time_type,
			getShiftTypes,
			getSelectableTimeTypes,
			getDefaultTimeType,
			getSelectableDayTypes,
			getAllGroups,
		} = this.props;

		if (!shift_types || shift_types.length === 0) {
			getShiftTypes();
		}

		if (!select_time_types || select_time_types.length === 0) {
			getSelectableTimeTypes();
		}

		if (!default_time_type) {
			getDefaultTimeType();
		}

		if (!select_day_types || select_day_types.length === 0) {
			getSelectableDayTypes();
			const selected_day_types = [];
			if (!this.state.isEdit) {
				const { select_day_types } = this.props;

				// Seleccionar todos los días de la semana (1-7)
				for (let i = 1; i <= 7; i++) {
					const day_type = select_day_types.find((item) => item.weekday === i);
					if (day_type) {
						selected_day_types.push(day_type);
					}
				}

				// Seleccionar también el tipo de día festivo
				const holiday_day_type = select_day_types.find(
					(item) => item.holiday === true
				);
				if (
					holiday_day_type &&
					!selected_day_types.find((item) => item.id === holiday_day_type.id)
				) {
					selected_day_types.push(holiday_day_type);
				}

				this.setState({
					selected_day_types,
				});
			}
		}

		if (!groups || groups.length === 0) {
			getAllGroups();
		}

		if (!this.state.isEdit && (this.state.shift.type === "Trabajo" || this.state.shift.type === "Automático")) {
			const hasPrefilledInterval =
				this.props.initialInterval?.start &&
				this.props.initialInterval?.end;

			if (hasPrefilledInterval) {
				this.focusNameField();
			} else {
				this.focusTimeIntervalEditorStart();
			}
		} else if (this.state.shift.type === "Ausencia" || this.state.shift.type === "Vacaciones") {
			this.focusNameField();
		}

		// Agregar event listener para detectar clics fuera del picker
		document.addEventListener("click", this.handleDocumentClick);

		this.syncInitialIntervalPreset();
	}

	componentDidUpdate(prevProps) {
		const timeTypesChanged =
			prevProps.select_time_types !== this.props.select_time_types;
		const defaultTypeChanged =
			prevProps.default_time_type !== this.props.default_time_type;

		if (timeTypesChanged || defaultTypeChanged) {
			this.syncInitialIntervalPreset();
		}
	}

	handleChange = (event) => {
		const { shift, errors } = this.state;
		const nextShift = {
			...shift,
			[event.target.id]: event.target.value,
		};
		const nextErrors = { ...errors };

		if (event.target.id === "name" && nextShift.name.trim()) {
			delete nextErrors.name;
		}

		this.setState({
			shift: nextShift,
			errors: nextErrors,
		});
	};

	handleSwitch = (event) => {
		const { shift } = this.state;

		this.setState({
			shift: {
				...shift,
				[event.target.id]: event.target.checked,
			},
		});
	};

	handleColorChange = (hex, type) => {
		const { shift } = this.state;

		if (hex === undefined) hex = "";

		this.setState({
			shift: {
				...shift,
				[type]: hex,
			},
			isBackgroundPickerVisible: false,
			isBorderPickerVisible: false,
		});
	};

	handleColorUpdate = (hex, type, shouldClose = false) => {
		const { shift } = this.state;

		if (hex === undefined) hex = "";

		this.setState({
			shift: {
				...shift,
				[type]: hex,
			},
			// Solo cerrar el picker cuando se selecciona un color por defecto
			isBackgroundPickerVisible:
				type === "background_color" && shouldClose
					? false
					: this.state.isBackgroundPickerVisible,
			isBorderPickerVisible:
				type === "border_color" && shouldClose
					? false
					: this.state.isBorderPickerVisible,
		});
	};

	checkValidField = (name) => {
		return (
			this.state.submitted &&
			this.state.errors[name] !== undefined &&
			this.state.errors[name] !== ""
		);
	};

	// Función para cerrar el picker cuando se hace clic fuera
	handleClickOutside = (event, pickerType) => {
		// Verificar si el clic fue fuera del picker
		const pickerElement = event.target.closest(".simple-color-picker");
		const colorButton = event.target.closest(".form-color");

		// No cerrar si el clic fue en un input del picker (hex o rgb)
		const isInputField = event.target.tagName === "INPUT" && pickerElement;

		if (!pickerElement && !colorButton && !isInputField) {
			this.setState({
				[pickerType]: false,
			});
		}
	};

	componentWillUnmount() {
		// Remover event listener al desmontar el componente
		document.removeEventListener("click", this.handleDocumentClick);
	}

	handleDocumentClick = (event) => {
		const { isBackgroundPickerVisible, isBorderPickerVisible } = this.state;

		if (isBackgroundPickerVisible) {
			this.handleClickOutside(event, "isBackgroundPickerVisible");
		}

		if (isBorderPickerVisible) {
			this.handleClickOutside(event, "isBorderPickerVisible");
		}
	};

	prepareDayTypesToSubmit = () => {
		const { selected_day_types } = this.state;

		return selected_day_types.map((day_type) => day_type.id);
	};

	prepareGroupsToSubmit = () => {
		const { selected_groups } = this.state;

		return selected_groups.map((group) => group.id);
	};

	prepareTimeIntervalsToSubmit = () => {
		const { shift } = this.state;

		return shift.time_intervals.map((time_interval) => {
			const new_interval = {
				start: time_interval.start,
				end: time_interval.end,
				time_type_id: time_interval.time_type_id,
			};

			if (!isNaN(time_interval.id)) new_interval.id = time_interval.id;

			if (new_interval.time_type_id === undefined)
				new_interval.time_type_id = time_interval.time_type.id;

			return new_interval;
		});
	};

	validateForm = () => {
		const { shift } = this.state;
		const errors = {};

		if (!shift.name || !shift.name.trim()) {
			errors.name = this.props.t("shifts.form.nameRequired");
		}

		return errors;
	};

	handleSubmit = async (event) => {
		event.preventDefault();
		const { isEdit, shift } = this.state;
		if ((shift.type === "Trabajo" || shift.type === "Automático") && this.state.isAddingInterval) {
			const shouldValidateDraft = !isEdit || this.hasDraftIntervalData();
			if (shouldValidateDraft) {
				const addedDraft = await this.addTimeInterval();
				if (!addedDraft) return;
			}
		}

		const clientErrors = this.validateForm();
		if (Object.keys(clientErrors).length > 0) {
			this.setState({
				submitted: true,
				errors: clientErrors,
			});
			return;
		}

		this.setState({ isLoading: true, errors: {} });
		let response = "";
		const props = this.props;

		const shift_temp = {
			...shift,
			day_type_ids: this.prepareDayTypesToSubmit(),
			group_ids: this.prepareGroupsToSubmit(),
			time_intervals: this.prepareTimeIntervalsToSubmit(),
		};

		// TEMP fix mentre el backend no valida que hi hagi group_ids quan has_groups és true
		if (shift.has_groups && shift_temp.group_ids.length === 0)
			shift_temp.has_groups = false;

		shift_temp.send_payflow_on_leave =
			shift_temp.type === "Ausencia" && Boolean(shift_temp.send_payflow_on_leave);

		if (shift_temp.max_per_year < 0) shift_temp.max_per_year = null;

		if (shift_temp.month_limit < 0) shift_temp.month_limit = null;

		if (shift_temp.day_limit < 0) shift_temp.day_limit = null;

		// if edit => update; else => new
		if (isEdit) {
			response = await shiftsService.update(shift_temp);
		} else {
			response = await shiftsService.add(shift_temp);
		}

		// console.log(response);

		if (response.ok) {
			this.setState(
				{
					isLoading: false,
					errors: {},
				},
				() => {
					toastr.success(this.props.t("common.success"), this.props.t("common.changesSaved"));
					this.props.getShifts();
					props.handleClose();
				}
			);
		} else {
			this.setState({
				isLoading: false,
				submitted: true,
				errors: this.extendedErrorFormatting(response),
			});
		}
	};

	extendedErrorFormatting = (response) => {
		const errors = errorFormating(response);

		if (response.errors.length > 0) {
			response.errors.map((item) => {
				if (item.key.includes("time_intervals")) {
					errors.time_intervals = item.description;
				}
			});
		}
		return errors;
	};

	// add/remove tipos de día
	addDayType = () => {
		let { selected_day_types, new_day_type } = this.state;
		let isValid = true;
		let day_type;
		const errors = {};

		if (new_day_type === undefined) {
			isValid = false;
		} else {
			day_type = this.props.select_day_types.find(
				(item) => item.id === parseInt(new_day_type)
			);
			if (day_type === undefined) {
				isValid = false;
				errors.day_type = false;
			}
		}

		if (isValid) {
			// check if day already exists
			const exists = selected_day_types.find(
				(item) => item.id === parseInt(new_day_type)
			);

			if (!exists) {
				selected_day_types = selected_day_types.concat(day_type);
			}

			this.setState({
				selected_day_types,
				errors: {},
				new_day_type: {},
			});
		} else {
			this.setState({
				errors,
			});
		}
	};

	addBulkDayTypes = (start, end) => {
		const selected_day_types = [];
		const { select_day_types } = this.props;

		for (let i = start; i <= end; i++) {
			const day_type = select_day_types.find((item) => item.weekday === i);
			if (day_type) {
				selected_day_types.push(day_type);
			}
		}

		this.setState({
			selected_day_types,
			errors: {},
			new_day_type: {},
		});
	};

	removeDayType = (idDayType) => {
		this.setState((prevState, props) => ({
			selected_day_types: prevState.selected_day_types.filter(
				(item) => item.id !== parseInt(idDayType)
			),
		}));
	};

	toggleGroup = (group) => {
		this.setState((prev) => {
			const list = prev.selected_groups || [];
			const exists = list.some((item) => item.id === group.id);
			return {
				selected_groups: exists
					? list.filter((item) => item.id !== group.id)
					: list.concat(group),
			};
		});
	};

	clearGroups = () => {
		this.setState({ selected_groups: [] });
	};

	// time intervals
	handleChangeInterval = (event) => {
		const { new_interval } = this.state;
		const key = event.target.id;

		this.setState({
			new_interval: {
				...new_interval,
				[key]: event.target.value,
			},
			// selected_timetype
		});
	};

	addTimeInterval = ({ keepEditorOpen = false } = {}) => {
		const { shift, new_interval } = this.state;
		let isValid = true;
		const new_interval_errors = {};
		const time_type = this.props.select_time_types.find(
			(dt) => dt.id === parseInt(new_interval.time_type_id)
		);
		const default_time_type = this.props.default_time_type;

		if (!time_type) {
			isValid = false;
			new_interval_errors.time_type_id = false;
		}

		if (!new_interval.start) {
			isValid = false;
			new_interval_errors.start = false;
		}

		if (!new_interval.end) {
			isValid = false;
			new_interval_errors.end = false;
		}

		if (isValid) {
			new_interval.time_type = time_type;

			// Asume que convertToUTC ya no existe: hacemos todo aquí
			const tz = moment.tz.guess();
			// Partimos de la fecha de hoy
			const base = moment().startOf("day"); // e.g. 2025-05-06T00:00 en tu zona

			// Creamos dos moments locales con fecha “hoy”
			let mStart = base
				.clone()
				.hour(+new_interval.start.split(":")[0])
				.minute(+new_interval.start.split(":")[1]);
			let mEnd = base
				.clone()
				.hour(+new_interval.end.split(":")[0])
				.minute(+new_interval.end.split(":")[1]);

			// Si el fin “local” es antes del inicio → cruza medianoche → sumamos 1 día
			if (mEnd.isBefore(mStart)) {
				mEnd.add(1, "day");
			}

			// Ahora sí los pasamos a UTC, sin cambiar de fecha de referencia
			const startUTC = mStart.tz(tz).utc().format("HH:mm[Z]");
			const endUTC = mEnd.tz(tz).utc().format("HH:mm[Z]");

			// Asignamos las versiones convertidas a UTC al intervalo
			const converted_interval = {
				...new_interval,
				start: startUTC,
				end: endUTC,
			};

			let time_intervals = shift.time_intervals.concat(converted_interval);

			time_intervals = this.sortTimeIntervalsByLocalStart(time_intervals);

			const show_timetype_disclaimer =
				this.checkTimeTypeDisponibilidad(time_intervals);

			return new Promise((resolve) => {
				const { time_intervals_by_type } = this.state;
				const updatedIntervalsByType = { ...time_intervals_by_type };
				if (WORK_SHIFT_TYPES.includes(shift.type)) {
					updatedIntervalsByType[shift.type] = time_intervals;
				}

				this.setState(
					{
						shift: {
							...shift,
							time_intervals,
						},
						time_intervals_by_type: updatedIntervalsByType,
						new_interval: this.buildDefaultNewInterval(shift.type),
						new_interval_errors: {},
						show_timetype_disclaimer,
						isAddingInterval: keepEditorOpen,
					},
					() => {
						if (keepEditorOpen) {
							this.focusTimeIntervalEditorStart();
						}
						resolve(true);
					}
				);
			});
		} else {
			this.setState({
				new_interval_errors,
			});
			return Promise.resolve(false);
		}
	};

	getTimeInMinutes = (hhmm = "") => {
		const [hours, minutes] = hhmm.split(":").map(Number);
		if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
		return hours * 60 + minutes;
	};

	sortTimeIntervalsByLocalStart = (intervals = []) =>
		[...intervals].sort((a, b) => {
			const aStartLocal = convertFromUTC(a.start);
			const bStartLocal = convertFromUTC(b.start);
			return this.getTimeInMinutes(aStartLocal) - this.getTimeInMinutes(bStartLocal);
		});

	startAddingInterval = () => {
		this.setState({ isAddingInterval: true }, () => {
			// Espera al render para desplazar y enfocar el campo Inicio.
			this.focusTimeIntervalEditorStart();
		});
	};

	handleAddPeriodClick = () => {
		if (this.state.isAddingInterval) {
			this.addTimeInterval({ keepEditorOpen: true });
			return;
		}
		this.startAddingInterval();
	};

	cancelAddingInterval = () => {
		const default_time_type = this.props.default_time_type;
		this.setState({
			isAddingInterval: false,
			new_interval: {
				time_type_id: default_time_type ? default_time_type.id : "",
				start: "",
				end: "",
			},
			new_interval_errors: {},
		});
	};

	clearDraftInterval = () => {
		this.setState({
			isAddingInterval: false,
			new_interval: {
				time_type_id: "",
				start: "",
				end: "",
			},
			new_interval_errors: {},
		});
	};

	hasDraftIntervalData = () => {
		const { new_interval } = this.state;
		return Boolean(
			new_interval.start || new_interval.end || new_interval.time_type_id
		);
	};

	checkTimeTypeDisponibilidad = (time_intervals, shiftTypeValue = this.state.shift.type) => {
		if (shiftTypeValue === "Automático") return false;
		if (time_intervals.length === 0) return false;

		return time_intervals.find(
			(item) => item.time_type?.name === "Disponibilidad"
		);
	};

	removeTimeInterval = (interval) => {
		const { shift, time_intervals_by_type } = this.state;
		const time_intervals = shift.time_intervals.filter(
			(item) =>
				(item.start === interval.start &&
					item.end === interval.end &&
					item.time_type_id === interval.time_type_id) === false
		);
		const show_timetype_disclaimer =
			this.checkTimeTypeDisponibilidad(time_intervals);
		const updatedIntervalsByType = { ...time_intervals_by_type };
		if (WORK_SHIFT_TYPES.includes(shift.type)) {
			updatedIntervalsByType[shift.type] = time_intervals;
		}

		this.setState({
			shift: {
				...shift,
				time_intervals,
			},
			time_intervals_by_type: updatedIntervalsByType,
			show_timetype_disclaimer,
		});
	};

	getSelectedDayTypes = () =>
		this.state.selected_day_types.length > 0 &&
		this.state.selected_day_types.map((item) => {
			return (
				<li
					key={item.id}
					aria-label={item.description}
					title={item.description}
					className="tag-default tag-delete"
					style={{
						backgroundColor: item.background_color && item.background_color,
					}}
				>
					{item.name}
					<button
						type="button"
						onClick={() => this.removeDayType(item.id)}
						className="btn-tag-delete btn-transparent"
					>
						<FiX />
					</button>
				</li>
			);
		});


	getDayTypeOptions = () => {
		const { select_day_types } = this.props;
		const weekdays = select_day_types
			.filter((item) => item.weekday)
			.sort((a, b) => a.weekday - b.weekday);
		const specialDayTypes = select_day_types.filter((item) => !item.weekday);
		const orderedDayTypes = [...weekdays, ...specialDayTypes];

		return orderedDayTypes.map((item) => ({
			value: item.id,
			label: item.name,
		}));
	};

	handleTypeChange = (type) => {
		this.setState((prevState) => {
			const { shift, time_intervals_by_type } = prevState;
			const prevType = shift.type;
			const updatedIntervalsByType = { ...time_intervals_by_type };

			if (WORK_SHIFT_TYPES.includes(prevType)) {
				updatedIntervalsByType[prevType] = shift.time_intervals;
			}

			let nextTimeIntervals = shift.time_intervals;
			if (WORK_SHIFT_TYPES.includes(type)) {
				nextTimeIntervals = updatedIntervalsByType[type] || [];
			} else {
				nextTimeIntervals = [];
			}

			return {
				shift: {
					...shift,
					type,
					time_intervals: nextTimeIntervals,
				},
				time_intervals_by_type: updatedIntervalsByType,
				show_timetype_disclaimer: this.checkTimeTypeDisponibilidad(
					nextTimeIntervals,
					type
				),
				new_interval: this.buildDefaultNewInterval(type),
				new_interval_errors: {},
			};
		}, () => {
			if (type === "Ausencia" || type === "Vacaciones") {
				this.focusNameField();
			}
		});
	};

	handleDayTypesSelection = (selectedIds = []) => {
		const { select_day_types } = this.props;
		const selected_day_types = select_day_types.filter((item) =>
			selectedIds.includes(item.id)
		);

		this.setState((prevState) => ({
			selected_day_types,
			errors: {
				...prevState.errors,
				day_type_ids: "",
			},
		}));
	};

	getTimeIntervalCards = () =>
		this.sortTimeIntervalsByLocalStart(this.state.shift.time_intervals).map(
			(interval, i) => {
			const interval_start = convertFromUTC(interval.start);
			const interval_end = convertFromUTC(interval.end);

			return (
				<TimeIntervalCard
					key={i}
					typeValue={interval.time_type.name}
					startValue={interval_start}
					endValue={interval_end}
					onRemove={() => this.removeTimeInterval(interval)}
				/>
			);
			}
		);

	render() {
		const {
			shift,
			errors,
			new_interval,
			new_interval_errors,
			selected_day_types,
			selected_groups,
			show_timetype_disclaimer,
			isAddingInterval,
		} = this.state;
		const { groups, select_time_types } = this.props;

		// Turnos de trabajo: excluir Disponibilidad
		const work_time_list = select_time_types.filter(
			(t) => t.name !== "Disponibilidad"
		);
		const optionTimeTypeList =
			work_time_list.length > 0 &&
			work_time_list.map((item, i) => (
				<option key={i} value={item.id}>
					{item.name}
				</option>
			));

		// Turnos automáticos (IA): solo Disponibilidad
		const availability_time_list = select_time_types.filter(
			(t) => t.name === "Disponibilidad"
		);
		const optionAvailabilityTimeTypeList =
			availability_time_list.length > 0 &&
			availability_time_list.map((item, i) => (
				<option key={i} value={item.id}>
					{item.name}
				</option>
			));

		const rest_time_list = select_time_types.filter(
			(t) => t.work_time === false
		);
		const optionRestTimeTypeList = rest_time_list.map((item, i) => (
			<option key={i} value={item.id}>
				{item.name}
			</option>
		));

		const optionLimit = Array.from({ length: 51 }, (_, i) => (
			<option key={i} value={i}>
				{i}
			</option>
		));

		const optionDayLimit = Array.from({ length: 31 }, (_, i) => (
			<option key={i} value={i + 1}>
				{i + 1}
			</option>
		));

		const optionMonthLimit = this.props.t("shifts.form.months", { returnObjects: true }).map((month, i) => (
			<option key={i} value={i + 1}>
				{month}
			</option>
		));

		const onToggleBackgroundPicker = () =>
			this.setState((prevState) => ({
				isBackgroundPickerVisible: !prevState.isBackgroundPickerVisible,
				isBorderPickerVisible: false,
			}));
		const onToggleBorderPicker = () =>
			this.setState((prevState) => ({
				isBorderPickerVisible: !prevState.isBorderPickerVisible,
				isBackgroundPickerVisible: false,
			}));

		const allowHourly = shift.allow_hourly_absence_request;
		const sendPayflowOnLeave = Boolean(shift.send_payflow_on_leave);
		const allowOverlap = shift.allow_overlap;
		const hasGroups = shift.has_groups;
		const isActive = shift.active;
		const dayTypeOptions = this.getDayTypeOptions();
		const selectedDayTypeIds = selected_day_types.map((item) => item.id);

		return (
			<form onSubmit={this.handleSubmit}>
				<div className="modal-body shift-edit">
				<PillToggleGroup
					className="shift-edit__type-pills"
					ariaLabel={this.props.t("shifts.form.shiftType")}
					variant="segmented"
					options={[
						{ value: "Trabajo", label: this.props.t("shifts.form.typeWork") },
						{ value: "Automático", label: this.props.t("shifts.form.typeAutomatic") },
						{ value: "Ausencia", label: this.props.t("shifts.form.typeAbsence") },
						{ value: "Vacaciones", label: this.props.t("shifts.form.typeVacation") },
					]}
					value={shift.type}
					onChange={this.handleTypeChange}
				/>
				{shift.type === "Automático" && (
					<ContextualHelpBanner className="shift-edit__automatic-help">
						{this.props.t("shifts.form.automaticHelp")}
					</ContextualHelpBanner>
				)}
					<Row className="shift-edit__main-fields">
						<Col sm={6} md={3} className="shift-edit__field shift-edit__field--name">
							<FormTextField
								controlId="name"
								label={this.props.t("shifts.form.shortName")}
								required
								value={shift.name}
								onChange={this.handleChange}
								placeholder={this.props.t("shifts.form.namePlaceholder")}
								isInvalid={this.checkValidField("name")}
								error={errors.name}
							/>
						</Col>
						<Col
							sm={6}
							md={3}
							className="shift-edit__field shift-edit__field--description"
						>
							<FormTextField
								controlId="description"
								label={this.props.t("shifts.form.description")}
								value={shift.description}
								onChange={this.handleChange}
								placeholder={this.props.t("shifts.form.descriptionPlaceholder")}
								isInvalid={this.checkValidField("description")}
								error={errors.description}
							/>
						</Col>

						<Col
							sm={6}
							md={3}
							className="shift-edit__field shift-edit__field--background-color"
						>
							<ShiftColorField
								controlId="background_color"
								label={this.props.t("shifts.form.backgroundColor")}
								value={shift.background_color}
								isInvalid={this.checkValidField("background_color")}
								error={errors.background_color}
								onClear={() => this.handleColorChange("", "background_color")}
								onTogglePicker={onToggleBackgroundPicker}
								isPickerVisible={this.state.isBackgroundPickerVisible}
								colors={SHIFT_COLORS}
								onColorChange={(hex, shouldClose) =>
									this.handleColorUpdate(hex, "background_color", shouldClose)
								}
								onClosePicker={() =>
									this.setState({ isBackgroundPickerVisible: false })
								}
							/>
						</Col>
						<Col
							sm={6}
							md={3}
							className="shift-edit__field shift-edit__field--border-color"
						>
							<ShiftColorField
								controlId="border_color"
								label={this.props.t("shifts.form.borderColor")}
								value={shift.border_color}
								isInvalid={this.checkValidField("border_color")}
								error={errors.border_color}
								onClear={() => this.handleColorChange("", "border_color")}
								onTogglePicker={onToggleBorderPicker}
								isPickerVisible={this.state.isBorderPickerVisible}
								colors={SHIFT_COLORS}
								onColorChange={(hex, shouldClose) =>
									this.handleColorUpdate(hex, "border_color", shouldClose)
								}
								onClosePicker={() =>
									this.setState({ isBorderPickerVisible: false })
								}
							/>
						</Col>
					</Row>

					{(shift.type === "Ausencia" || shift.type === "Vacaciones") && (
						<SwitchField
							controlId="allow_hourly_absence_request"
							className={`form-group shift-edit__switch-field shift-edit__hourly-switch ${
								allowHourly ? "shift-edit__hourly-switch--active" : ""
							}`}
							compact
							isOn={allowHourly}
							onToggle={this.handleSwitch}
							label={`${this.props.t("shifts.form.allowHourlyPrefix")} ${this.props.t(shift.type === "Ausencia" ? "shifts.form.typeAbsence" : "shifts.form.typeVacation")} ${this.props.t("shifts.form.allowHourlySuffix")}`}
							labelClassName="label-normal"
						/>
					)}

					{shift.type === "Ausencia" && (
						<div className="shift-edit__payflow-leave">
							<SwitchField
								controlId="send_payflow_on_leave"
								className="form-group shift-edit__switch-field"
								compact
								isOn={sendPayflowOnLeave}
								onToggle={this.handleSwitch}
								label={this.props.t("shifts.form.sendPayflowOnLeave")}
								labelClassName="label-normal"
							/>
							<p className="shift-edit__payflow-leave-help">
								{this.props.t("shifts.form.sendPayflowOnLeaveHelp")}
							</p>
						</div>
					)}

					{shift.type === "Ausencia" && allowHourly && (
						<FormGroup
							className="form-group shift-edit__hourly-time-type"
							controlId="time_type_id"
						>
							<FormLabel>
								{this.props.t("shifts.form.selectTimeType")}{" "}
								<span className="label-required">*</span>
							</FormLabel>
							<FormControl
								placeholder={this.props.t("shifts.form.selectTypePlaceholder")}
								isInvalid={this.checkValidField("time_type_id")}
								onChange={this.handleChange}
								value={shift.time_type_id}
								as="select"
							>
								<option value="">{this.props.t("shifts.form.select")}</option>
								{optionRestTimeTypeList}
							</FormControl>

							<FormControl.Feedback type="invalid">
								{errors.time_type_id}
							</FormControl.Feedback>
						</FormGroup>
					)}

				{(shift.type === "Trabajo" || shift.type === "Automático") && (
					<div className="time-intervals">

							<div className="row-special-days flex-between gap10 shift-edit__interval-actions">
							<h3 className="subtitle">{this.props.t("shifts.form.timePeriods")}</h3>
								<button
									type="button"
									className="btn btn-link shift-edit__add-interval"
									onClick={this.handleAddPeriodClick}
								>
									<BsFillPlusCircleFill />
									<span>{this.props.t("shifts.form.addPeriod")}</span>
								</button>
							</div>

							{errors.time_intervals && (
								<InlineAlert variant="error" className="mt15 mb15">
									{errors.time_intervals}
								</InlineAlert>
							)}
							{show_timetype_disclaimer && (
								<div className="alert alert-info time-interval-disclaimer">
									<p>
										{this.props.t("shifts.form.availabilityDisclaimer1")}
									</p>
									<ul>
										<li>7 a 15h</li>
										<li>15 a 23h</li>
										<li>23 a 7h</li>
									</ul>
									<p>
										{this.props.t("shifts.form.availabilityDisclaimer2")}
									</p>
								</div>
							)}

						{shift.time_intervals.length > 0 && (
							<div className="time-interval-card-list">
								{this.getTimeIntervalCards()}
							</div>
						)}

						{isAddingInterval && (
							<TimeIntervalEditor
								editorRef={this.timeIntervalEditorRef}
								newInterval={new_interval}
								newIntervalErrors={new_interval_errors}
								optionTimeTypeList={
									shift.type === "Automático"
										? optionAvailabilityTimeTypeList
										: optionTimeTypeList
								}
								onChange={this.handleChangeInterval}
								onRemoveDraft={this.clearDraftInterval}
							/>
						)}
					</div>
				)}

					<div className="row-adding row-adding-days shift-edit__days-section">
						<FormLabel>
							{this.props.t("shifts.form.dayTypesLabel")}{" "}
							<span className="label-required">*</span>
						</FormLabel>
						<Pills
							ariaLabel={this.props.t("shifts.form.dayTypes")}
							options={dayTypeOptions}
							value={selectedDayTypeIds}
							onChange={this.handleDayTypesSelection}
						/>
						{this.checkValidField("day_type_ids") && (
							<div className="invalid-feedback d-block">{errors.day_type_ids}</div>
						)}
					</div>

					{(shift.type === "Ausencia" || shift.type === "Vacaciones") && (
						<div className="shift-limits box-info pb0 mb25">
							<h3 className="subtitle">{this.props.t("shifts.form.limits")}</h3>

							<Row>
								<Col sm={6} md={4}>
									<FormGroup className="form-group" controlId="max_per_year">
										<FormLabel>{this.props.t("shifts.form.maxPerYear")}</FormLabel>
										<FormControl
											value={shift.max_per_year}
											onChange={this.handleChange}
											placeholder="-"
											isInvalid={this.checkValidField("max_per_year")}
											as="select"
										>
											<option value="-1">-</option>
											{optionLimit}
										</FormControl>

										<FormControl.Feedback type="invalid">
											{errors.max_per_year}
										</FormControl.Feedback>
									</FormGroup>
								</Col>
								<Col sm={12} md={8}>
									<FormLabel>{this.props.t("shifts.form.deadline")}</FormLabel>

									<FormGroup className="form-group fecha-limite">
										<FormControl
											id="day_limit"
											value={shift.day_limit}
											onChange={this.handleChange}
											placeholder="-"
											as="select"
											className="day-limit"
										>
											<option value="-1">-</option>
											{optionDayLimit}
										</FormControl>
										<FormControl
											id="month_limit"
											value={shift.month_limit}
											onChange={this.handleChange}
											placeholder="-"
											as="select"
										>
											<option value="-1">-</option>
											{optionMonthLimit}
										</FormControl>
									</FormGroup>
								</Col>
							</Row>
						</div>
					)}

					<div className="shift-edit__switch-stack">
						<SwitchField
							controlId="allow_overlap"
							className="form-group shift-edit__switch-field"
							compact
							isOn={allowOverlap}
							onToggle={this.handleSwitch}
							label={this.props.t("shifts.form.allowOverlap")}
							labelClassName="label-normal"
						/>

						<SwitchField
							controlId="has_groups"
							className="form-group shift-edit__switch-field shift-edit__switch-field--has-groups"
							compact
							isOn={hasGroups}
							onToggle={this.handleSwitch}
							label={this.props.t("shifts.form.groupsOnly")}
							labelClassName="label-normal"
						/>
					</div>

					{hasGroups && (
						<div className="row-adding">
							<CatalogMultiSelect
								label={
									<>
										{this.props.t("shifts.form.groups")}{" "}
										<span className="label-required">*</span>
									</>
								}
								items={groups}
								selected={selected_groups}
								labelOf={(group) =>
									group.description
										? `${group.name} ${group.description}`
										: group.name
								}
								chipOf={(group) =>
									catalogChip(
										group,
										group.name,
										this.props.t("common.delete")
									)
								}
								searchPlaceholder={this.props.t("common.catalog.searchGroup")}
								onToggleItem={this.toggleGroup}
								onClear={this.clearGroups}
								invalid={this.checkValidField("group_ids")}
								error={
									this.checkValidField("group_ids") ? errors.group_ids : null
								}
							/>
						</div>
					)}

					<SwitchField
						controlId="active"
						className="form-group"
						compact
						isOn={isActive}
						onToggle={this.handleSwitch}
						label={this.props.t("shifts.form.active")}
						labelClassName="label-normal"
					/>
				</div>

				<div className="modal-footer">
					<button
						type="button"
						onClick={this.props.handleClose}
						className="btn btn-outline-primary"
					>
						{this.props.t("common.cancel")}
					</button>
					<SubmitButton
						type="submit"
						isLoading={this.state.isLoading}
						text={this.props.t("common.save")}
						loadingText={this.props.t("common.saving")}
					/>
				</div>
			</form>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.calendarsReducer,
		...reducers.timesReducer,
		...reducers.shiftsReducer,
		...reducers.groupsReducer,
		loading: reducers.shiftsReducer.loading,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getAllShifts: (options) => dispatch(shiftsActions.getAllShifts(options)),
	getShiftTypes: () => dispatch(shiftsActions.getShiftTypes()),
	getAllGroups: () => dispatch(groupsActions.getAllGroups()),
	getSelectableTimeTypes: () => dispatch(timesActions.getSelectableTimeTypes()),
	getDefaultTimeType: () => dispatch(timesActions.getDefaultTimeType()),
	getSelectableDayTypes: () =>
		dispatch(calendarsActions.getSelectableDayTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ShiftEdit));
